import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';

export interface IErrorHistoryEntry {
  id: string;
  title: string;
  message?: string;
  timestamp: number;
}

export interface IErrorHistoryContextValue {
  errors: IErrorHistoryEntry[];
  unreadCount: number;
  addError: (entry: { title: string; message?: string }) => void;
  markAllRead: () => void;
  clear: () => void;
}

const MAX_HISTORY_ENTRIES = 50;
const STORAGE_KEY = 'nuxnode-error-history';
const LAST_READ_STORAGE_KEY = 'nuxnode-error-history-last-read';

const noop = (): void => undefined;

const defaultContextValue: IErrorHistoryContextValue = {
  errors: [],
  unreadCount: 0,
  addError: noop,
  markAllRead: noop,
  clear: noop,
};

const ErrorHistoryContext = createContext<IErrorHistoryContextValue>(defaultContextValue);

function generateId(): string {
  if (typeof globalThis !== 'undefined' && globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readErrorsFromStorage(): IErrorHistoryEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readLastReadFromStorage(): number {
  try {
    const raw = localStorage.getItem(LAST_READ_STORAGE_KEY);
    const parsed = raw ? Number(raw) : 0;

    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeErrorsToStorage(errors: IErrorHistoryEntry[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(errors));
  } catch {
    // Storage may be unavailable (e.g. private browsing quota) - history stays in-memory only.
  }
}

function writeLastReadToStorage(timestamp: number): void {
  try {
    localStorage.setItem(LAST_READ_STORAGE_KEY, String(timestamp));
  } catch {
    // Storage may be unavailable (e.g. private browsing quota) - unread state stays in-memory only.
  }
}

export const ErrorHistoryProvider = ({ children }: PropsWithChildren): ReactNode => {
  const [errors, setErrors] = useState<IErrorHistoryEntry[]>(readErrorsFromStorage);
  const [lastReadAt, setLastReadAt] = useState<number>(readLastReadFromStorage);

  const addError = useCallback((entry: { title: string; message?: string }) => {
    setErrors((prev) => {
      const next = [
        { id: generateId(), title: entry.title, message: entry.message, timestamp: Date.now() },
        ...prev,
      ].slice(0, MAX_HISTORY_ENTRIES);

      writeErrorsToStorage(next);

      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    const now = Date.now();

    setLastReadAt(now);
    writeLastReadToStorage(now);
  }, []);

  const clear = useCallback(() => {
    setErrors([]);
    writeErrorsToStorage([]);
  }, []);

  const unreadCount = useMemo(
    () => errors.filter((entry) => entry.timestamp > lastReadAt).length,
    [errors, lastReadAt],
  );

  const value = useMemo<IErrorHistoryContextValue>(
    () => ({ errors, unreadCount, addError, markAllRead, clear }),
    [errors, unreadCount, addError, markAllRead, clear],
  );

  return <ErrorHistoryContext.Provider value={value}>{children}</ErrorHistoryContext.Provider>;
};

export const useErrorHistory = (): IErrorHistoryContextValue => useContext(ErrorHistoryContext);
